import {
  AccountCircle as AccountCircleIcon,
  Add as AddIcon,
  Analytics as AnalyticsIcon,
  CheckCircle as CheckCircleIcon,
  ChevronLeft as ChevronLeftIcon,
  Circle as CircleIcon,
  CreditCard as CreditCardIcon,
  Dashboard as DashboardIcon,
  ExitToApp as ExitToAppIcon,
  ExpandLess,
  ExpandMore,
  Folder as FolderIcon,
  Groups as GroupsIcon,
  History as HistoryIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  LocalDining as LocalDiningIcon,
  LocalOffer as LocalOfferIcon,
  Menu as MenuIcon,
  Palette as PaletteIcon,
  PauseCircleOutline as PauseCircleOutlineIcon,
  Payment as PaymentIcon,
  Receipt as ReceiptIcon,
  Logo,
  RestaurantMenu as RestaurantMenuIcon,
  Settings as SettingsIcon,
  Store as StoreIcon,
  TableRestaurant as TableRestaurantIcon,
} from '../icons';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  Tooltip,
  useMediaQuery,
  Badge,
  Collapse,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useAuth } from '../../contexts/AuthContext';
import { useMerchant } from '../../contexts/MerchantContext';
import { useNotification } from '../../contexts/NotificationContext';
import { useOrder } from '../../contexts/OrderContext';
import {
  FEATURE_SUBSCRIPTION_ENFORCEMENT,
  useFeatureFlag,
} from '../../contexts/FeatureFlagContext';
import AdminNotificationContainer from '../notifications/AdminNotificationContainer';
import NotificationBell from '../notifications/NotificationBell';
import SubscriptionNavStatus from '../subscription/SubscriptionNavStatus';
import { BRAND, SUCCESS, WARNING, ERROR, NEUTRAL } from '../../theme/adminColors';

const drawerWidthDefault = 260;
const drawerWidthCompact = 220;
const miniDrawerWidth = 70;
const SIDEBAR_MINI_KEY = 'adminSidebarMini';

// Main AdminLayout component
const AdminLayout = ({ children }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser } = useAuth();
  const {
    currentMerchant,
    availableMerchants,
    switchMerchant,
    merchantChangeKey,
    loading: merchantLoading,
    canManageMenu,
    canViewAnalytics,
    canManageTables,
    canManagePayments,
    getCurrentUserRole,
    merchantStatus,
    updateMerchantStatus,
  } = useMerchant();
  const { initializeAudio } = useNotification();
  const { wsConnectionStatus, ordersByStatus, updateCounter } = useOrder(); // Get WebSocket status and orders from global context
  const subscriptionEnforcementEnabled = useFeatureFlag(FEATURE_SUBSCRIPTION_ENFORCEMENT);
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Small tablets / narrow landscape screens get a slimmer drawer so the
  // permanent sidebar doesn't dominate the viewport.
  const isCompactViewport = useMediaQuery(theme.breakpoints.down('lg'));
  const drawerWidth = isCompactViewport ? drawerWidthCompact : drawerWidthDefault;

  const [open, setOpen] = useState(!isMobile);
  const [miniVariant, setMiniVariant] = useState(() => {
    if (typeof window === 'undefined' || isMobile) return false;
    return window.localStorage.getItem(SIDEBAR_MINI_KEY) === 'true';
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [showOrdersBadge, setShowOrdersBadge] = useState(true);
  const [switchingMerchant, setSwitchingMerchant] = useState(false);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [pauseAnchorEl, setPauseAnchorEl] = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);

  const STATUS_CONFIG = {
    available: { label: 'Available', color: SUCCESS.main, bg: SUCCESS.softHover },
    busy: { label: 'Busy', color: WARNING.main, bg: WARNING.softHover },
    paused: { label: 'Paused', color: ERROR.main, bg: ERROR.softHover },
  };

  const currentStatusConfig = STATUS_CONFIG[merchantStatus] || STATUS_CONFIG.available;

  const handleStatusMenuOpen = (event) => {
    setStatusAnchorEl(event.currentTarget);
  };

  const handleStatusMenuClose = () => {
    setStatusAnchorEl(null);
    setPauseAnchorEl(null);
  };

  const handleStatusChange = async (newStatus, pauseMode = null) => {
    setStatusUpdating(true);
    handleStatusMenuClose();
    try {
      await updateMerchantStatus(newStatus, pauseMode);
    } catch (err) {
      console.error('Failed to update status:', err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handlePauseMenuOpen = (event) => {
    setPauseAnchorEl(event.currentTarget);
  };

  const handlePauseMenuClose = () => {
    setPauseAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    if (isMobile) {
      setOpen(!open);
    } else {
      // On desktop, toggle between full open, mini, and closed
      if (open && !miniVariant) {
        setMiniVariant(true);
      } else if (open && miniVariant) {
        setOpen(false);
        setMiniVariant(false);
      } else {
        setOpen(true);
        setMiniVariant(false);
      }
    }
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/', { replace: true });
  };

  const handleMenuToggle = () => {
    setMenuOpen(!menuOpen);
  };

  const handleHistoryToggle = () => {
    setHistoryOpen(!historyOpen);
  };

  const handleSettingsToggle = () => {
    setSettingsOpen(!settingsOpen);
  };

  const handleMerchantSwitch = async (merchantId) => {
    // Don't switch if it's the current merchant or already switching
    if (merchantId === currentMerchant?.id || switchingMerchant) {
      handleProfileMenuClose();
      return;
    }

    try {
      setSwitchingMerchant(true);

      handleProfileMenuClose();

      await switchMerchant(merchantId);

    } catch (error) {
      console.error('❌ Failed to switch merchant:', error);
      alert('Failed to switch merchant. Please try again.');
    } finally {
      setSwitchingMerchant(false);
    }
  };

  const handleCreateMerchant = () => {
    handleProfileMenuClose();
    navigate('/setup-merchant');
  };


  const handleOrdersClick = () => {
    // Clear the badge when orders is clicked and store current count
    setShowOrdersBadge(false);
    localStorage.setItem('ordersBadgeAcknowledgedCount', ordersBadgeCount.toString());
  };

  // Calculate the badge count (pending + preparing orders)
  const ordersBadgeCount = React.useMemo(() => {
    if (!ordersByStatus) return 0;
    const pendingCount = ordersByStatus.pending?.length || 0;
    const preparingCount = ordersByStatus.preparing?.length || 0;
    return pendingCount + preparingCount;
  }, [ordersByStatus, updateCounter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load badge state from localStorage
  useEffect(() => {
    const acknowledgedCount = localStorage.getItem('ordersBadgeAcknowledgedCount');
    if (acknowledgedCount) {
      const lastCount = parseInt(acknowledgedCount);
      // Only hide badge if count hasn't increased since last acknowledgment
      if (ordersBadgeCount <= lastCount) {
        setShowOrdersBadge(false);
      }
    }
  }, [ordersBadgeCount]);

  // Show badge when new orders come in via WebSocket
  useEffect(() => {
    if (ordersBadgeCount > 0) {
      const acknowledgedCount = localStorage.getItem('ordersBadgeAcknowledgedCount');

      if (acknowledgedCount) {
        const lastCount = parseInt(acknowledgedCount);
        // If order count increased, show badge again (new orders came in)
        if (ordersBadgeCount > lastCount) {
          setShowOrdersBadge(true);
        }
      } else {
        // No previous acknowledgment, show badge
        setShowOrdersBadge(true);
      }
    } else {
      // No orders, hide badge
      setShowOrdersBadge(false);
    }
  }, [ordersBadgeCount]);

  // Initialize audio on first user interaction
  useEffect(() => {
    const handleFirstInteraction = async () => {
      try {
        await initializeAudio();
      } catch (_) { /* audio init is best-effort */ }
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };

    document.addEventListener('click', handleFirstInteraction, { once: true });
    document.addEventListener('keydown', handleFirstInteraction, { once: true });

    return () => {
      document.removeEventListener('click', handleFirstInteraction);
      document.removeEventListener('keydown', handleFirstInteraction);
    };
  }, [initializeAudio]);

  // Persist the collapsed (mini) sidebar preference across reloads.
  useEffect(() => {
    if (isMobile) return;
    window.localStorage.setItem(SIDEBAR_MINI_KEY, String(miniVariant));
  }, [miniVariant, isMobile]);

  // Auto-expand the submenu that contains the active route.
  useEffect(() => {
    const path = location.pathname;
    if (path.startsWith('/admin/menu')) setMenuOpen(true);
    if (path.startsWith('/admin/orders/history') || path.startsWith('/admin/payments')) {
      setHistoryOpen(true);
    }
    if (path.startsWith('/admin/settings') || path.startsWith('/admin/billing')) {
      setSettingsOpen(true);
    }
  }, [location.pathname]);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const currentRole = getCurrentUserRole();
  const isStaffRole = currentRole === 'staff';
  const permissionsReady = !merchantLoading && currentRole !== null;

  const allMenuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/admin',
      active: isActive('/admin')
    },
    {
      text: 'Menu',
      icon: <RestaurantMenuIcon />,
      path: '/admin/menu',
      active: isActive('/admin/menu') || location.pathname.startsWith('/admin/menu'),
      visible: permissionsReady && (!isStaffRole || canManageMenu()),
      subMenu: true,
      open: menuOpen,
      onToggle: handleMenuToggle,
      subItems: [
        { text: 'Categories', path: '/admin/menu/categories', icon: <FolderIcon /> },
        { text: 'Items', path: '/admin/menu/items', icon: <LocalDiningIcon /> },
        { text: 'Options', path: '/admin/menu/options', icon: <LocalOfferIcon /> }
      ]
    },
    {
      text: 'Orders',
      icon: <ReceiptIcon />,
      path: '/admin/orders/board',
      active: isActive('/admin/orders/board'),
      showBadge: showOrdersBadge && ordersBadgeCount > 0,
      badgeCount: ordersBadgeCount,
      onClick: handleOrdersClick
    },
    {
      text: 'Analytics',
      icon: <AnalyticsIcon />,
      path: '/admin/orders/analytics',
      active: isActive('/admin/orders/analytics'),
      visible: permissionsReady && (!isStaffRole || canViewAnalytics())
    },
    {
      text: 'Table QR',
      icon: <TableRestaurantIcon />,
      path: '/admin/tables',
      active: isActive('/admin/tables'),
      visible: permissionsReady && (!isStaffRole || canManageTables())
    },
    {
      text: 'History',
      icon: <HistoryIcon />,
      path: '/admin/orders/history',
      active: isActive('/admin/orders/history') || isActive('/admin/payments'),
      subMenu: true,
      open: historyOpen,
      onToggle: handleHistoryToggle,
      subItems: [
        { text: 'Orders', path: '/admin/orders/history', icon: <ReceiptIcon /> },
        ...((!isStaffRole || canManagePayments())
          ? [{ text: 'Payments', path: '/admin/payments', icon: <PaymentIcon /> }]
          : []),
      ]
    },
    {
      text: 'Settings',
      icon: <SettingsIcon />,
      path: '/admin/settings',
      active: isActive('/admin/settings') || location.pathname.startsWith('/admin/settings') || location.pathname.startsWith('/admin/billing'),
      subMenu: true,
      open: settingsOpen,
      onToggle: handleSettingsToggle,
      subItems: isStaffRole
        ? [
            { text: 'Profile', path: '/admin/settings/profile', icon: <AccountCircleIcon /> },
            { text: 'Members', path: '/admin/settings/members', icon: <GroupsIcon /> },
          ]
        : [
            { text: 'Profile', path: '/admin/settings/profile', icon: <AccountCircleIcon /> },
            { text: 'General', path: '/admin/settings/general', icon: <SettingsIcon /> },
            { text: 'Branding', path: '/admin/settings/branding', icon: <PaletteIcon /> },
            { text: 'Orders', path: '/admin/settings/orders', icon: <ReceiptIcon /> },
            { text: 'Payments', path: '/admin/settings/payments', icon: <PaymentIcon /> },
            { text: 'Members', path: '/admin/settings/members', icon: <GroupsIcon /> },
            ...(subscriptionEnforcementEnabled
              ? [{ text: 'Billing', path: '/admin/billing', icon: <CreditCardIcon /> }]
              : []),
          ]
    }
  ];

  const menuItems = allMenuItems.filter(item => item.visible !== false);

  const drawer = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {!miniVariant && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2.5,
              minHeight: 64,
            }}
          >
            <Box
              component={RouterLink}
              to="/admin"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                textDecoration: 'none',
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.05)',
                },
              }}
            >
              <Logo size={36} />
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 800,
                  color: BRAND.main,
                  letterSpacing: '-0.5px',
                }}
              >
                merchQRy
              </Typography>
            </Box>
            <IconButton
              onClick={handleDrawerToggle}
              size="small"
              sx={{
                '&:hover': {
                  backgroundColor: BRAND.soft,
                }
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Box>
          <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.08)' }} />
        </>
      )}

      {miniVariant && (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              py: 2,
              minHeight: 64,
            }}
          >
            <IconButton
              onClick={handleDrawerToggle}
              size="small"
              sx={{
                '&:hover': {
                  backgroundColor: BRAND.soft,
                }
              }}
            >
              <Logo size={32} />
            </IconButton>
          </Box>
          <Divider sx={{ borderColor: 'rgba(0, 0, 0, 0.08)' }} />
        </>
      )}

      <List component="nav" aria-label="Main navigation">
        {menuItems.map((item) => (
          <React.Fragment key={item.text}>
            {miniVariant ? (
              <Tooltip title={item.text} placement="right">
                <ListItem
                  button
                  component={item.subMenu ? 'div' : RouterLink}
                  to={item.subMenu ? undefined : item.path}
                  aria-label={item.text}
                  aria-current={!item.subMenu && item.active ? 'page' : undefined}
                  aria-expanded={item.subMenu ? item.open : undefined}
                  onClick={() => {
                    if (item.subMenu) {
                      setMiniVariant(false);
                      item.onToggle();
                    } else {
                      if (item.onClick) item.onClick();
                      if (isMobile) setOpen(false);
                    }
                  }}
                  sx={{
                    minHeight: 52,
                    justifyContent: 'center',
                    px: 2.5,
                    my: 0.5,
                    mx: 1,
                    borderRadius: '12px',
                    bgcolor: item.active ? BRAND.softHover : 'transparent',
                    '&:hover': {
                      bgcolor: item.active ? BRAND.softHover : 'rgba(0, 0, 0, 0.04)',
                      transform: 'translateX(4px)',
                    },
                    '&.Mui-focusVisible': {
                      bgcolor: BRAND.soft,
                      outline: `2px solid ${BRAND.border}`,
                      outlineOffset: '-2px',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: 0,
                      justifyContent: 'center',
                      color: item.active ? BRAND.main : NEUTRAL.textSecondary,
                    }}
                  >
                    {item.showBadge ? (
                      <Badge
                        badgeContent={item.badgeCount}
                        color="error"
                        max={99}
                        sx={{
                          '& .MuiBadge-badge': {
                            fontSize: '0.65rem',
                            height: '18px',
                            minWidth: '18px',
                            borderRadius: '9px',
                            fontWeight: 700,
                            backgroundColor: ERROR.main,
                            boxShadow: `0 2px 8px ${ERROR.shadow}`,
                          }
                        }}
                      >
                        {item.icon}
                      </Badge>
                    ) : (
                      item.icon
                    )}
                  </ListItemIcon>
                </ListItem>
              </Tooltip>
            ) : (
              <ListItem
                button
                component={item.subMenu ? 'div' : RouterLink}
                to={item.subMenu ? undefined : item.path}
                aria-current={!item.subMenu && item.active ? 'page' : undefined}
                aria-expanded={item.subMenu ? item.open : undefined}
                onClick={() => {
                  if (item.subMenu) {
                    item.onToggle();
                  } else {
                    if (item.onClick) item.onClick();
                    if (isMobile) setOpen(false);
                  }
                }}
                sx={{
                  position: 'relative',
                  my: 0.5,
                  mx: 1.5,
                  borderRadius: '12px',
                  bgcolor: item.active ? BRAND.softHover : 'transparent',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 3,
                    height: item.active ? '55%' : '0%',
                    borderRadius: '0 4px 4px 0',
                    backgroundColor: BRAND.main,
                    transition: 'height 0.2s ease',
                  },
                  '&:hover': {
                    bgcolor: item.active ? BRAND.softHover : 'rgba(0, 0, 0, 0.04)',
                    transform: 'translateX(4px)',
                  },
                  '&.Mui-focusVisible': {
                    bgcolor: BRAND.soft,
                    outline: `2px solid ${BRAND.border}`,
                    outlineOffset: '-2px',
                  },
                  transition: 'all 0.2s ease',
                  py: 1.2,
                  pr: 2,
                }}
              >
                <ListItemIcon
                  sx={{
                    color: item.active ? BRAND.main : NEUTRAL.textSecondary,
                    minWidth: 42,
                  }}
                >
                  {item.showBadge ? (
                    <Badge
                      badgeContent={item.badgeCount}
                      color="error"
                      max={99}
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          height: '18px',
                          minWidth: '18px',
                          borderRadius: '9px',
                          fontWeight: 700,
                          backgroundColor: ERROR.main,
                          boxShadow: `0 2px 8px ${ERROR.shadow}`,
                        }
                      }}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    color: item.active ? BRAND.main : NEUTRAL.text,
                    fontWeight: item.active ? 700 : 600,
                    fontSize: '0.95rem',
                  }}
                />
                {item.subMenu && (item.open ?
                  <ExpandLess sx={{ flexShrink: 0, fontSize: 20, color: item.active ? BRAND.main : NEUTRAL.textMuted }} /> :
                  <ExpandMore sx={{ flexShrink: 0, fontSize: 20, color: item.active ? BRAND.main : NEUTRAL.textMuted }} />
                )}
              </ListItem>
            )}

            {item.subMenu && !miniVariant && (
              <Collapse in={item.open} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.subItems.map((subItem) => (
                    <ListItem
                      key={subItem.text}
                      button
                      component={RouterLink}
                      to={subItem.path}
                      aria-current={isActive(subItem.path) ? 'page' : undefined}
                      onClick={() => {
                        if (isMobile) {
                          setOpen(false);
                        }
                      }}
                      sx={{
                        pl: subItem.icon ? 4 : 6,
                        pr: 1.5,
                        py: 1,
                        my: 0.3,
                        mx: 1.5,
                        borderRadius: '10px',
                        bgcolor: isActive(subItem.path) ? BRAND.soft : 'transparent',
                        '&:hover': {
                          bgcolor: isActive(subItem.path) ? BRAND.softHover : 'rgba(0, 0, 0, 0.03)',
                          transform: 'translateX(4px)',
                        },
                        '&.Mui-focusVisible': {
                          bgcolor: BRAND.soft,
                          outline: `2px solid ${BRAND.border}`,
                          outlineOffset: '-2px',
                        },
                        transition: 'all 0.2s ease',
                      }}
                    >
                      {subItem.icon && (
                        <ListItemIcon
                          sx={{ minWidth: 32, color: isActive(subItem.path) ? BRAND.main : '#6b7280' }}
                        >
                          {React.cloneElement(subItem.icon, { sx: { fontSize: 18 } })}
                        </ListItemIcon>
                      )}
                      <ListItemText
                        primary={subItem.text}
                        primaryTypographyProps={{
                          color: isActive(subItem.path) ? BRAND.main : '#4b5563',
                          fontWeight: isActive(subItem.path) ? 600 : 500,
                          fontSize: '0.9rem',
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        ))}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      <SubscriptionNavStatus mini={miniVariant} />
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', width: '100%', minWidth: 0, overflowX: 'clip' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          width: `calc(100% - ${
            !isMobile && (open || miniVariant)
              ? (miniVariant ? miniDrawerWidth : drawerWidth)
              : 0
          }px)`,
          ml: !isMobile && (open || miniVariant)
            ? (miniVariant ? `${miniDrawerWidth}px` : `${drawerWidth}px`)
            : 0,
          backgroundColor: BRAND.main,
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar sx={{ py: 1, px: { xs: 1, sm: 2 }, gap: { xs: 0.5, sm: 1 } }}>
          {(isMobile || (!open && !miniVariant)) && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              onClick={handleDrawerToggle}
              edge="start"
              sx={{
                mr: { xs: 0.5, sm: 2 },
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                }
              }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, minWidth: 0 }}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{
                fontWeight: 700,
                letterSpacing: '-0.5px',
                fontSize: { xs: '0.95rem', sm: '1.15rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {currentMerchant?.name || 'merchQRy Admin'}
            </Typography>

            {/* WebSocket Status Indicator */}
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor:
                  wsConnectionStatus === 'CONNECTED' ? SUCCESS.main :
                  wsConnectionStatus === 'CONNECTING' ? WARNING.main : ERROR.main,
                boxShadow: wsConnectionStatus === 'CONNECTED'
                  ? `0 0 8px ${SUCCESS.main}`
                  : wsConnectionStatus === 'CONNECTING'
                    ? `0 0 8px ${WARNING.main}`
                    : `0 0 8px ${ERROR.main}`,
              }}
            />
          </Box>

          {/* Notification Bell */}
          <Box sx={{ flexShrink: 0 }}>
            <NotificationBell />
          </Box>

          {/* Status Dropdown */}
          <Box sx={{ flexShrink: 0 }}>
            <Box
              onClick={handleStatusMenuOpen}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.75,
                cursor: 'pointer',
                borderRadius: '20px',
                px: { xs: 1, sm: 1.5 },
                py: 0.5,
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.25)',
                },
                transition: 'all 0.2s ease',
                opacity: statusUpdating ? 0.6 : 1,
                pointerEvents: statusUpdating ? 'none' : 'auto',
              }}
            >
              <CircleIcon sx={{ fontSize: 10, color: currentStatusConfig.color }} />
              <Typography
                sx={{
                  display: { xs: 'none', sm: 'block' },
                  color: '#fff',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  lineHeight: 1,
                }}
              >
                {currentStatusConfig.label}
              </Typography>
              <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'rgba(255, 255, 255, 0.5)', ml: -0.25 }} />
            </Box>
            <Menu
              anchorEl={statusAnchorEl}
              open={Boolean(statusAnchorEl)}
              onClose={handleStatusMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: {
                  mt: 1,
                  borderRadius: '12px',
                  minWidth: 180,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              <MenuItem
                onClick={() => handleStatusChange('available')}
                selected={merchantStatus === 'available'}
                sx={{ gap: 1.5, py: 1.25 }}
              >
                <CircleIcon sx={{ fontSize: 12, color: STATUS_CONFIG.available.color }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Available</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Business as usual</Typography>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={() => handleStatusChange('busy')}
                selected={merchantStatus === 'busy'}
                sx={{ gap: 1.5, py: 1.25 }}
              >
                <CircleIcon sx={{ fontSize: 12, color: STATUS_CONFIG.busy.color }} />
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Busy</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Orders may take longer</Typography>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={handlePauseMenuOpen}
                selected={merchantStatus === 'paused'}
                sx={{ gap: 1.5, py: 1.25 }}
              >
                <PauseCircleOutlineIcon sx={{ fontSize: 14, color: STATUS_CONFIG.paused.color }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Pause</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>Stop accepting orders</Typography>
                </Box>
                <KeyboardArrowDownIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
              </MenuItem>
            </Menu>
            <Menu
              anchorEl={pauseAnchorEl}
              open={Boolean(pauseAnchorEl)}
              onClose={handlePauseMenuClose}
              anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              PaperProps={{
                sx: {
                  ml: 0.5,
                  borderRadius: '12px',
                  minWidth: 200,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                },
              }}
            >
              <MenuItem
                onClick={() => { handlePauseMenuClose(); handleStatusChange('paused', 'eod'); }}
                sx={{ gap: 1.5, py: 1.25 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>Until End of Day</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    Resumes next business day
                  </Typography>
                </Box>
              </MenuItem>
              <MenuItem
                onClick={() => { handlePauseMenuClose(); handleStatusChange('paused', '24h'); }}
                sx={{ gap: 1.5, py: 1.25 }}
              >
                <Box>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.875rem' }}>For 24 Hours</Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                    Resumes after 24 hours
                  </Typography>
                </Box>
              </MenuItem>
            </Menu>
          </Box>

          <Box
            onClick={handleProfileMenuOpen}
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: { xs: 0.75, sm: 1 },
              cursor: 'pointer',
              borderRadius: '28px',
              pl: { xs: 0.5, sm: 0.75 },
              pr: { xs: 0.5, sm: 1.5 },
              py: 0.5,
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.15)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                border: '1px solid rgba(255, 255, 255, 0.25)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <Avatar
              sx={{
                width: { xs: 30, sm: 32 },
                height: { xs: 30, sm: 32 },
                fontSize: { xs: '0.8rem', sm: '0.85rem' },
                fontWeight: 600,
                bgcolor: 'rgba(255, 255, 255, 0.25)',
                color: '#fff',
              }}
              src={currentUser?.avatar}
            >
              {currentUser?.first_name?.[0] || currentUser?.email?.[0]?.toUpperCase() || 'U'}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'flex' }, flexDirection: 'column', minWidth: 0 }}>
              <Typography
                noWrap
                sx={{
                  color: '#fff',
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  lineHeight: 1.3,
                  maxWidth: 140,
                }}
              >
                {currentUser?.first_name
                  ? `${currentUser.first_name} ${currentUser.last_name || ''}`.trim()
                  : currentUser?.email || ''}
              </Typography>
              <Typography
                noWrap
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  fontSize: '0.65rem',
                  lineHeight: 1.2,
                  fontWeight: 500,
                  textTransform: 'capitalize',
                  maxWidth: 140,
                }}
              >
                {getCurrentUserRole() || 'Member'}
              </Typography>
            </Box>
            <ExpandMore
              sx={{
                display: { xs: 'none', sm: 'block' },
                color: 'rgba(255, 255, 255, 0.5)',
                fontSize: '1.1rem',
                ml: -0.25,
              }}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {(isMobile || open || miniVariant) && (
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? open : true}
          onClose={isMobile ? handleDrawerToggle : undefined}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            width: miniVariant && !isMobile ? miniDrawerWidth : drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: miniVariant && !isMobile ? miniDrawerWidth : drawerWidth,
              boxSizing: 'border-box',
              borderRight: 'none',
              backgroundColor: '#ffffff',
              boxShadow: isMobile ? '0 8px 32px rgba(0, 0, 0, 0.12)' : '0 4px 20px rgba(0, 0, 0, 0.06)',
              overflowX: 'hidden',
              transition: theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            },
          }}
        >
          {drawer}
        </Drawer>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          minHeight: '100vh',
          width: '100%',
          maxWidth: '100%',
          overflowX: 'clip',
          backgroundColor: NEUTRAL.page,
          marginTop: '64px', // Add space for the AppBar
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23667eea' fill-rule='evenodd' fill-opacity='0.02'%3E%3Cpath d='M50 50c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10zm10 0c0-5.5-4.5-10-10-10s-10 4.5-10 10 4.5 10 10 10 10-4.5 10-10z'/%3E%3C/g%3E%3C/svg%3E")`,
            pointerEvents: 'none',
          },
        }}
      >
        <Box
          key={`merchant-${currentMerchant?.id}-${merchantChangeKey}`}
          sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: '100%' }}
        >
          {children}
        </Box>
      </Box>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        PaperProps={{
          sx: {
            minWidth: 320,
            maxHeight: 600,
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
            border: `1px solid ${BRAND.soft}`,
            mt: 1
          }
        }}
      >
        {/* Merchant Switcher Section */}
        {availableMerchants && availableMerchants.length >= 1 && (
          <>
            <Box sx={{
              px: 3,
              py: 2,
              backgroundColor: BRAND.soft,
              borderBottom: `1px solid ${BRAND.soft}`
            }}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  letterSpacing: '0.5px',
                  color: BRAND.main,
                  textTransform: 'uppercase',
                  fontSize: '0.7rem'
                }}
              >
                <StoreIcon sx={{ fontSize: 'inherit', verticalAlign: '-0.125em', mr: 0.5 }} />
                {availableMerchants.length > 1 ? 'Switch Merchant' : 'My Merchant'}
              </Typography>
            </Box>

            <Box sx={{ maxHeight: 300, overflowY: 'auto', py: 1 }}>
              {availableMerchants.map((merchant) => (
                <MenuItem
                  key={merchant.id}
                  onClick={() => handleMerchantSwitch(merchant.id)}
                  disabled={switchingMerchant || availableMerchants.length === 1}
                  sx={{
                    py: 1.5,
                    px: 2.5,
                    mx: 1,
                    my: 0.5,
                    borderRadius: '12px',
                    bgcolor: merchant.id === currentMerchant?.id ? BRAND.soft : 'transparent',
                    border: merchant.id === currentMerchant?.id ? `2px solid ${BRAND.border}` : '2px solid transparent',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: merchant.id === currentMerchant?.id ? BRAND.softHover : 'rgba(0, 0, 0, 0.04)',
                      transform: 'translateX(4px)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {merchant.id === currentMerchant?.id ? (
                      <CheckCircleIcon
                        sx={{
                          color: BRAND.main,
                          fontSize: '1.3rem'
                        }}
                      />
                    ) : (
                      <StoreIcon
                        sx={{
                          color: NEUTRAL.textSecondary,
                          fontSize: '1.3rem'
                        }}
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{
                            fontWeight: merchant.id === currentMerchant?.id ? 700 : 500,
                            color: merchant.id === currentMerchant?.id ? NEUTRAL.text : '#4b5563',
                            fontSize: '0.9rem'
                          }}
                        >
                          {merchant.name}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Chip
                            label={(merchant.user_role || 'staff').charAt(0).toUpperCase() + (merchant.user_role || 'staff').slice(1)}
                            size="small"
                            sx={{
                              height: 22,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                              bgcolor: merchant.user_role === 'owner'
                                ? 'rgba(239, 68, 68, 0.1)'
                                : merchant.user_role === 'manager'
                                ? 'rgba(251, 146, 60, 0.1)'
                                : 'rgba(59, 130, 246, 0.1)',
                              color: merchant.user_role === 'owner'
                                ? ERROR.main
                                : merchant.user_role === 'manager'
                                ? '#fb923c'
                                : '#3b82f6',
                              border: `1px solid ${
                                merchant.user_role === 'owner'
                                  ? 'rgba(239, 68, 68, 0.2)'
                                  : merchant.user_role === 'manager'
                                  ? 'rgba(251, 146, 60, 0.2)'
                                  : 'rgba(59, 130, 246, 0.2)'
                              }`
                            }}
                          />
                        </Box>
                      </Box>
                    }
                  />
                </MenuItem>
              ))}
            </Box>

            <MenuItem
              onClick={handleCreateMerchant}
              sx={{
                py: 1.75,
                px: 2.5,
                mx: 1,
                my: 1,
                borderRadius: '12px',
                backgroundColor: BRAND.soft,
                border: `2px dashed ${BRAND.border}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: BRAND.softHover,
                  borderColor: BRAND.border,
                  transform: 'translateX(4px)',
                }
              }}
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <AddIcon
                  sx={{
                    color: BRAND.main,
                    fontSize: '1.3rem',
                    fontWeight: 700
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body2"
                    sx={{
                      color: BRAND.main,
                      fontWeight: 700,
                      fontSize: '0.9rem'
                    }}
                  >
                    Create New Merchant
                  </Typography>
                }
              />
            </MenuItem>

            <Divider sx={{ my: 1 }} />
          </>
        )}

        {/* Regular Profile Menu Items */}
        <Box sx={{ px: 1, py: 0.5 }}>
          <MenuItem
            onClick={() => { handleProfileMenuClose(); navigate('/admin/settings/profile'); }}
            sx={{
              borderRadius: '12px',
              py: 1.5,
              px: 2,
              mb: 0.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: BRAND.soft,
                transform: 'translateX(4px)',
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <AccountCircleIcon sx={{ color: BRAND.main, fontSize: '1.3rem' }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography sx={{ fontWeight: 500, color: NEUTRAL.text, fontSize: '0.9rem' }}>
                  Profile
                </Typography>
              }
            />
          </MenuItem>
          <MenuItem
            onClick={() => { handleProfileMenuClose(); navigate('/admin/settings'); }}
            sx={{
              borderRadius: '12px',
              py: 1.5,
              px: 2,
              mb: 0.5,
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: BRAND.soft,
                transform: 'translateX(4px)',
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <SettingsIcon sx={{ color: BRAND.main, fontSize: '1.3rem' }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography sx={{ fontWeight: 500, color: NEUTRAL.text, fontSize: '0.9rem' }}>
                  Settings
                </Typography>
              }
            />
          </MenuItem>
        </Box>

        <Divider sx={{ my: 1 }} />

        <Box sx={{ px: 1, pb: 1 }}>
          <MenuItem
            onClick={handleLogout}
            sx={{
              borderRadius: '12px',
              py: 1.5,
              px: 2,
              bgcolor: 'rgba(239, 68, 68, 0.05)',
              border: '1px solid rgba(239, 68, 68, 0.1)',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(239, 68, 68, 0.1)',
                borderColor: 'rgba(239, 68, 68, 0.2)',
                transform: 'translateX(4px)',
              }
            }}
          >
            <ListItemIcon sx={{ minWidth: 40 }}>
              <ExitToAppIcon sx={{ color: ERROR.main, fontSize: '1.3rem' }} />
            </ListItemIcon>
            <ListItemText
              primary={
                <Typography sx={{ fontWeight: 600, color: ERROR.main, fontSize: '0.9rem' }}>
                  Logout
                </Typography>
              }
            />
          </MenuItem>
        </Box>
      </Menu>


      {/* Admin Notifications - positioned at bottom-right */}
      <AdminNotificationContainer />
    </Box>
  );
};

export default AdminLayout;
